import { useContext, useEffect, useRef } from 'react';

import { EditStudentAccessDialog, EditStudentAccessDialogHandle } from 'pages/students/components/Transactions/components/edit_student_access_dialog/EditStudentAccessDialog';
import { TransactionGMetrixTableDialog, TransactionGMetrixTableDialogHandle } from 'components/TransactionTableGMetrixDialog/TransactionGMetrixTableDialog';
import { StudentDetailsContext, StudentsContext } from 'pages/students/context/StudentsDefaultContext';
import { GMetrixButtonDropdown } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { TransactionApi, TransactionReference } from 'apis/TransactionApi';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { TileContainer } from 'components/TileContainer/TileContainer';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { filterDataBySearchTerm } from 'utils';
import { useNavigate } from 'react-router-dom';
import { GMetrixTable } from 'components';
import { useJoyride } from 'app/joyride';
import { TransactionType } from 'types';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/students/components/Transactions/styles/transactions.scss';

type TransactionsProps = {
    studentId: number;
}

export const Transactions = (props: TransactionsProps) => {

    const { studentId } = props;

    const [state, setState] = useSuperState({
        loadingGridData: true,
        searchTerm: '',
        transactionReferences: [] as TransactionReference[]
    });

    const { isSystemMode, userId } = useApp();
    const { pageContext } = useContext(StudentDetailsContext);
    const { permissions } = useContext(StudentsContext);
    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const { loadCurrentTour } = useJoyride();

    const navigate = useNavigate();

    const gmetrixConfirmRef = useRef<any>(null);
    const addTransactionDialogRef = useRef<TransactionGMetrixTableDialogHandle>(null);
    const editStudentAccessDialogRef = useRef<EditStudentAccessDialogHandle>(null);

    const loadTransactions = async () => {

        try {

            const transactionReferences = await TransactionApi.GetStudentTransactions(studentId);
            setState({ transactionReferences });

        } catch (e) {

            catchWithToast(formatMessage({ id: 'failed_to_load_student_access_codes.dd34facd', defaultMessage: 'Failed to load student access codes.' }))(e);

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const deleteStudentFromGroup = async (dataItem: TransactionReference) => {

        if (dataItem && pageContext) {

            await TransactionApi.RemoveStudentFromTransaction([dataItem.StudentContentAccessId]);
            try {

                const transactionReferences = await TransactionApi.GetStudentTransactions(studentId);
                setState({ transactionReferences });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_reload_student_access_codes.f8ad51e7', defaultMessage: 'Failed to reload student access codes.' }))(e);

            } finally {

                setState({ loadingGridData: false });

            }

        }

    };

    const onDetailsClick = (transaction: TransactionReference) => {

        navigate(`/access-codes/${transaction.TransactionId}`);

    };

    useEffect(() => {

        loadCurrentTour('CodesTab');

    }, []);

    useEffect(() => {

        if (!state.addNewTransactionModal) {

            const loadTransactions = async () => {

                try {

                    const transactionReferences = await TransactionApi.GetStudentTransactions(studentId);
                    setState({ transactionReferences });

                } catch (e) {

                    catchWithToast(formatMessage({ id: 'failed_to_load_student_access_codes.dd34facd', defaultMessage: 'Failed to load student access codes.' }))(e);

                } finally {

                    setState({ loadingGridData: false });

                }

            };
            loadTransactions();

        }

    }, [state.addNewTransactionModal]);

    return (
        <div className="transactions">
            <TileContainer
                addButton
                showButtonAsDropDown
                buttonDropDownItemList={{
                    itemActionList: [async () => {

                        await addTransactionDialogRef.current?.show({
                            removeTheseTransactions: state.transactionReferences?.map(t => t.TransactionId),
                            transactionAssociation: {
                                associationIds: pageContext ? [pageContext.Id] : [],
                                associationName: 'Student'
                            }
                        });
                        loadTransactions();

                    }],
                    itemTextList: [formatMessage({ id: 'add_existing_access_codes.352d319e', defaultMessage: 'Add Existing Access Codes' })]
                }}
                addButtonDisabled={permissions!.PartnerType !== 4}
                addButtonDisabledTooltip={permissions!.PartnerType !== 4 ? 'Adding access codes is not available for this partner type.' : undefined}
                searchOptions={{ searchTerm: state.searchTerm, setSearchTerm: searchTerm => setState({ searchTerm }) }}
                showButtonAsDropDownText={formatMessage({ id: 'new_access_code.becdd264', defaultMessage: 'New Access Code' })}
            >
                <GMetrixTable
                    allowExport
                    columnData={[
                        { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                        {
                            cell: ({ dataItem }) => (
                                <td>
                                    {dataItem.Active
                                        ? <span className="fa-solid fa-check text-success kendoTableColumn__true" />
                                        : (
                                            <span className="text-danger fw-bold">
                                                <FormattedMessage defaultMessage="Inactive" description={formatMessage({ id: 'new.03d9347f', defaultMessage: 'New' })} id="inactive.5fb6d21a" />
                                            </span>
                                        )
                                    }
                                </td>
                            ),
                            field: 'Active',
                            title: formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })
                        },
                        {
                            cell: ({ dataItem }) => (
                                <td>
                                    {dataItem.CreatedDate
                                        ? new Date(dataItem.CreatedDate).toLocaleDateString()
                                        : ''}
                                </td>
                            ),
                            field: 'CreatedDate',
                            title: formatMessage({ id: 'date_added.88c093e2', defaultMessage: 'Date Added' })
                        },
                        {
                            cell: ({ dataItem }) => (
                                <td
                                    className={`${new Date(dataItem.ExpirationDate) < new Date()
                                        ? 'text-danger fw-bold'
                                        : ''
                                        }`}
                                >
                                    <FormattedDate value={new Date(dataItem.ExpirationDate)} />
                                </td>
                            ),
                            field: 'ExpirationDate',
                            title: formatMessage({ id: 'expires.84d49534', defaultMessage: 'Expires' })
                        },
                        {
                            cell: ({ dataItem }) => (
                                <td>
                                    <div className="d-flex justify-content-end mt-2 mb-2">
                                        {dataItem.TransactionType !== TransactionType.SCARecordWay ? (

                                            <GMetrixButtonDropdown
                                                connectedButton={(
                                                    <GMetrixButton
                                                        onClick={() => onDetailsClick(dataItem)}
                                                        variant="primary"
                                                    >
                                                        <FormattedMessage defaultMessage="Details" id="details.7799b923" />
                                                    </GMetrixButton>
                                                )}
                                                dataItems={[
                                                    ...(isSystemMode ? [{
                                                        action: () => {

                                                            editStudentAccessDialogRef.current?.show(dataItem.StudentContentAccessId);

                                                        }, text: formatMessage({ id: 'edit.00978daf', defaultMessage: 'Edit' })
                                                    }] : []),
                                                    {
                                                        action: async () => {

                                                            const result = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'remove_student_from_access_code.61e0c47b', defaultMessage: 'Remove Student from Access Code' }), formatMessage({ id: 'are_you_sure_you_want_to_remove_this_stu.cd95e6fc', defaultMessage: 'Are you sure you want to remove this Student from this Access Code?' }));
                                                            if (result) {

                                                                deleteStudentFromGroup(dataItem);

                                                            }

                                                        }, text: formatMessage({ id: 'remove.d34ac1e7', defaultMessage: 'Remove' })
                                                    }
                                                ]}
                                                iconClass="fa-solid fa-ellipsis-v"
                                                variant="primary"
                                            />

                                        ) :
                                            <div className="sizeBuffer"></div>}
                                    </div>
                                </td>
                            ),
                            width: 200
                        }
                    ]}
                    bottomBarTableName={formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' })}
                    loading={state.loadingGridData}
                    rowData={state.transactionReferences ? filterDataBySearchTerm(state.searchTerm, state.transactionReferences) : []}
                    searchPlaceholder={formatMessage({ id: 'search_by_name_code_or_description.73d39eef', defaultMessage: 'Search by name, code, or description...' })}
                />
                <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

                <TransactionGMetrixTableDialog
                    multiSelect
                    actionButtonText={formatMessage({ id: 'add_access_codes.a4a8853c', defaultMessage: 'Add Access Code(s)' })}
                    currentUserId={userId}
                    option="Add"
                    ref={addTransactionDialogRef}
                />

                <EditStudentAccessDialog
                    onSaved={loadTransactions}
                    ref={editStudentAccessDialogRef}
                />
            </TileContainer>
        </div>
    );

};
